import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from '@/utils/router';
import { BUSINESS_BASE } from '@/constants/core';
import { MEDIA_BREAKPOINTS } from '@/constants/layout';
import Footer from '@/components/Footer/Footer';
import { scheduleSpaReadyAfterPaint } from '@/utils/spaReady';

const Page = styled.div`
  min-height: 100vh;
  direction: rtl;
  color: #ffffff;
  font-family: ${({ theme }) => theme.fonts.rubikBoldItalic};
  background:
    radial-gradient(circle at 20% 0%, rgba(161, 39, 198, 0.24), transparent 34%),
    linear-gradient(145deg, #07070b 0%, #111116 48%, #050507 100%);
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(7, 7, 11, 0.88);
  backdrop-filter: blur(14px);
`;

const HeaderInner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 16px 22px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const Brand = styled(Link)`
  color: #ffffff;
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-weight: 900;
  letter-spacing: 0;
  text-decoration: none;
`;

const HomeLink = styled(Link)`
  color: rgba(255, 255, 255, 0.86);
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  padding: 8px 14px;
  text-decoration: none;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.44);
    outline: none;
  }
`;

const Main = styled.main`
  max-width: 1120px;
  margin: 0 auto;
  padding: 64px 22px 72px;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    padding: 40px 18px 52px;
  }
`;

const Hero = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(280px, 0.55fr);
  gap: 28px;
  align-items: stretch;
  margin-bottom: 28px;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-columns: 1fr;
  }
`;

const HeroPanel = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 34px;
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 22px 55px rgba(0, 0, 0, 0.28);

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    padding: 26px 20px;
  }
`;

const Eyebrow = styled.p`
  margin: 0 0 12px;
  color: #d9a8ff;
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-weight: 800;
  font-size: 0.95rem;
`;

const Title = styled.h1`
  margin: 0;
  max-width: 760px;
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: clamp(2.2rem, 5vw, 4.4rem);
  line-height: 1.06;
  letter-spacing: 0;
`;

const Lead = styled.p`
  margin: 20px 0 0;
  max-width: 760px;
  color: rgba(255, 255, 255, 0.84);
  font-size: clamp(1.05rem, 2vw, 1.25rem);
  line-height: 1.8;
`;

const StatusCard = styled.aside`
  border-radius: 8px;
  padding: 28px;
  background: #ffffff;
  color: #111116;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
`;

const StatusLabel = styled.p`
  margin: 0;
  color: #5e1779;
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-weight: 900;
`;

const StatusText = styled.p`
  margin: 10px 0 0;
  color: #2b2b31;
  line-height: 1.65;
`;

const DateBadge = styled.div`
  width: fit-content;
  border-radius: 999px;
  padding: 8px 12px;
  background: #f2e8f8;
  color: #4d1165;
  font-weight: 800;
  font-size: 0.9rem;
`;

const Grid = styled.section`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-top: 18px;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.055);

  h2 {
    margin: 0 0 12px;
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    font-size: 1.25rem;
  }

  p,
  li {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.75;
  }

  p {
    margin: 0;
  }

  ul {
    margin: 0;
    padding: 0 18px 0 0;
  }

  li + li {
    margin-top: 8px;
  }
`;

const ContactSection = styled.section`
  margin-top: 16px;
  border-radius: 8px;
  padding: 28px;
  background: linear-gradient(135deg, rgba(130, 48, 160, 0.92), rgba(70, 24, 95, 0.9));
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 20px;
  align-items: center;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-columns: 1fr;
  }

  h2 {
    margin: 0 0 8px;
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    font-size: 1.4rem;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.86);
    line-height: 1.7;
  }
`;

const ContactActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-start;
`;

const ContactButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  border-radius: 999px;
  padding: 10px 16px;
  background: #ffffff;
  color: #25102f;
  font-weight: 900;
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  text-decoration: none;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    background: #f3e9fb;
    outline: 2px solid rgba(255, 255, 255, 0.8);
    outline-offset: 2px;
  }
`;

const Note = styled.section`
  margin-top: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 22px 24px;
  background: rgba(0, 0, 0, 0.18);

  h2 {
    margin: 0 0 8px;
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    font-size: 1.18rem;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.78);
    line-height: 1.75;
  }
`;

const AccessibilityPage: React.FC = () => {
  const phoneHref = `tel:${BUSINESS_BASE.phone.replace(/[\s-]/g, '')}`;
  const emailHref = `mailto:${BUSINESS_BASE.email}`;

  useEffect(() => {
    return scheduleSpaReadyAfterPaint();
  }, []);

  return (
    <Page>
      <Header>
        <HeaderInner>
          <Brand to="/">די ג׳יי אליאור מור</Brand>
          <HomeLink to="/">חזרה לעמוד הבית</HomeLink>
        </HeaderInner>
      </Header>

      <Main>
        <Hero aria-labelledby="accessibility-title">
          <HeroPanel>
            <Eyebrow>נגישות ושירות שוויוני</Eyebrow>
            <Title id="accessibility-title">הצהרת נגישות</Title>
            <Lead>
              די ג׳יי אליאור מור רואה חשיבות במתן שירות נגיש, ברור ומכבד
              לכלל הגולשים והלקוחות. האתר נבנה מתוך מחשבה על חוויית שימוש
              נוחה, קריאה וזמינה ככל האפשר.
            </Lead>
          </HeroPanel>

          <StatusCard aria-label="סטטוס נגישות">
            <div>
              <StatusLabel>מצב האתר</StatusLabel>
              <StatusText>
                אנו פועלים לשיפור מתמשך של נגישות האתר, כולל מבנה תוכן
                ברור, תמיכה במסכים שונים ודרכי פנייה ישירות במקרה של קושי.
              </StatusText>
            </div>
            <DateBadge>עודכן: 5 ביוני 2026</DateBadge>
          </StatusCard>
        </Hero>

        <Grid>
          <Card>
            <h2>מה הותאם באתר?</h2>
            <ul>
              <li>מבנה עמודים ברור עם כותרות ותוכן קריא.</li>
              <li>אפשרות ניווט באמצעות מקלדת בדפדפנים מודרניים.</li>
              <li>התאמה למסכי מובייל, טאבלט ודסקטופ.</li>
              <li>שמירה על ניגודיות וקריאות בתוכן המרכזי.</li>
            </ul>
          </Card>

          <Card>
            <h2>תוכן ומדיה</h2>
            <p>
              באתר משולבים טקסטים, תמונות, סרטונים ומפות. אנו משתדלים לספק
              תיאורים וטקסטים ברורים לתוכן המרכזי, כדי להקל על הבנה וניווט.
            </p>
          </Card>

          <Card>
            <h2>שיפור מתמשך</h2>
            <p>
              אם מצאתם אזור באתר שאינו נוח או נגיש מספיק, נשמח לקבל דיווח.
              כל פנייה עוזרת לנו לשפר את החוויה עבור כלל המשתמשים.
            </p>
          </Card>
        </Grid>

        <ContactSection>
          <div>
            <h2>פנייה בנושא נגישות</h2>
            <p>
              נתקלתם בקושי בגלישה באתר או צריכים מידע בדרך חלופית? פנו אלינו
              ונעשה מאמץ לטפל בפנייה בהקדם.
            </p>
          </div>
          <ContactActions>
            <ContactButton href={phoneHref}>שיחה: {BUSINESS_BASE.phone}</ContactButton>
            <ContactButton href={emailHref}>שליחת אימייל</ContactButton>
          </ContactActions>
        </ContactSection>

        <Note>
          <h2>סייגים</h2>
          <p>
            האתר עשוי לכלול רכיבים חיצוניים כמו מפות, נגני וידאו או שירותים
            שאינם בשליטתנו המלאה. אם נתקלתם בבעיה ברכיב כזה, אפשר לפנות
            אלינו וננסה לסייע או להציע דרך חלופית לקבלת המידע.
          </p>
        </Note>
      </Main>

      <Footer />
    </Page>
  );
};

export default AccessibilityPage;
